import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Camera, Wand2, Loader2, Check, X, ImagePlus } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Profile Picture Picker Component
 * Allows users to select from generic avatars or generate custom AI avatars
 */
export const ProfilePicturePicker = ({ currentPicture, onUpdate, onClose }) => {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [generatedAvatar, setGeneratedAvatar] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("gallery"); // "gallery" or "ai"
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAvatars();
  }, []);

  const fetchAvatars = async () => {
    try {
      const response = await axios.get(`${API}/profile-pictures/avatars`);
      setAvatars(response.data.avatars || []);
    } catch (err) {
      console.error("Error fetching avatars:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAvatar = async () => {
    if (!prompt.trim()) {
      setError("Please enter a description for your avatar");
      return;
    }

    setError("");
    setGenerating(true);
    setGeneratedAvatar(null);

    try {
      const token = localStorage.getItem("userToken");
      const response = await axios.post(
        `${API}/profile-pictures/generate`,
        { prompt: prompt.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setGeneratedAvatar(response.data.image_url);
        setSelectedAvatar(null); // Deselect generic avatars
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to generate avatar. Please try again.";
      setError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectAvatar = (avatar) => {
    setSelectedAvatar(avatar);
    setGeneratedAvatar(null); // Clear generated avatar when selecting generic
    setError("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      const token = localStorage.getItem("userToken");
      let requestData;

      if (generatedAvatar) {
        requestData = { avatar_id: "custom", custom_url: generatedAvatar };
      } else if (selectedAvatar) {
        requestData = { avatar_id: selectedAvatar.id };
      } else {
        setError("Please select an avatar or generate one");
        setSaving(false);
        return;
      }

      const response = await axios.post(
        `${API}/profile-pictures/select`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update local storage with new user data
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        userData.profile_picture = response.data.profile_picture;
        localStorage.setItem("userData", JSON.stringify(userData));
        
        // Dispatch storage event to update other components
        window.dispatchEvent(new Event("storage"));
        
        // Callback to parent
        if (onUpdate) {
          onUpdate(response.data.profile_picture);
        }
        
        if (onClose) {
          onClose();
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save avatar");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("userToken");
      await axios.delete(`${API}/profile-pictures/remove`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      delete userData.profile_picture;
      localStorage.setItem("userData", JSON.stringify(userData));
      window.dispatchEvent(new Event("storage"));

      if (onUpdate) {
        onUpdate(null);
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError("Failed to remove avatar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        data-testid="profile-picture-picker"
      >
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-white/10 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-white" />
            <h2 className="font-secondary text-lg font-bold uppercase tracking-tight text-white">
              Profile Picture
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors"
            data-testid="close-picker-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Picture Preview */}
        <div className="p-4 border-b border-white/10 flex items-center gap-4">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center border-2 border-white/20">
            {(generatedAvatar || selectedAvatar?.url || currentPicture) ? (
              <img
                src={generatedAvatar || selectedAvatar?.url || currentPicture}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-10 h-10 text-zinc-500" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-white text-sm font-medium">Preview</p>
            <p className="text-zinc-500 text-xs">
              {generatedAvatar ? "AI Generated" : selectedAvatar ? selectedAvatar.name : "No picture selected"}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab("gallery")}
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
              activeTab === "gallery"
                ? "text-white border-b-2 border-white"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="gallery-tab"
          >
            <ImagePlus className="w-4 h-4 inline mr-2" />
            Gallery
          </button>
          <button
            onClick={() => setActiveTab("ai")}
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
              activeTab === "ai"
                ? "text-white border-b-2 border-white"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="ai-tab"
          >
            <Wand2 className="w-4 h-4 inline mr-2" />
            AI Generate
          </button>
        </div>

        <div className="p-4">
          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Gallery Tab */}
          {activeTab === "gallery" && (
            <div>
              <p className="text-zinc-400 text-xs mb-4">
                Choose from our collection of sports-themed avatars
              </p>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {avatars.map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => handleSelectAvatar(avatar)}
                      className={`relative aspect-square rounded-full overflow-hidden border-2 transition-all ${
                        selectedAvatar?.id === avatar.id
                          ? "border-green-500 ring-2 ring-green-500/50"
                          : "border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`avatar-${avatar.id}`}
                    >
                      <img
                        src={avatar.url}
                        alt={avatar.name}
                        className="w-full h-full object-cover"
                      />
                      {selectedAvatar?.id === avatar.id && (
                        <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                          <Check className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AI Generate Tab */}
          {activeTab === "ai" && (
            <div>
              <p className="text-zinc-400 text-xs mb-4">
                Describe your ideal profile picture and our AI will create it for you. 
                Keep it family-friendly and sports-related!
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-zinc-500 uppercase mb-1">
                    Describe your avatar
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="E.g., A cool basketball player with headband, abstract style"
                    className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm resize-none h-24"
                    maxLength={200}
                    disabled={generating}
                    data-testid="ai-prompt-input"
                  />
                  <p className="text-zinc-600 text-[10px] mt-1 text-right">
                    {prompt.length}/200 characters
                  </p>
                </div>

                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="text-amber-500">💡</span>
                  <span>Try: &quot;vintage football card style&quot;, &quot;neon basketball icon&quot;, &quot;minimalist soccer player&quot;</span>
                </div>

                <Button
                  onClick={handleGenerateAvatar}
                  disabled={generating || !prompt.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-secondary text-sm py-3 h-auto rounded-none"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating... (may take ~30s)
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Avatar
                    </>
                  )}
                </Button>

                <p className="text-zinc-600 text-[10px] text-center">
                  Limited to 5 generations per day. Inappropriate prompts will be rejected.
                </p>

                {/* Generated Avatar Preview */}
                {generatedAvatar && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/20"
                  >
                    <img
                      src={generatedAvatar}
                      alt="Generated avatar"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-green-400 text-sm font-medium flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Avatar Generated!
                      </p>
                      <p className="text-zinc-400 text-xs">Click &quot;Save&quot; to use this avatar</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-zinc-900 border-t border-white/10 p-4 flex gap-3">
          {currentPicture && (
            <Button
              onClick={handleRemove}
              disabled={saving}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 font-secondary text-xs px-4 py-2 h-auto rounded-none"
              data-testid="remove-avatar-btn"
            >
              Remove
            </Button>
          )}
          <div className="flex-1" />
          <Button
            onClick={onClose}
            variant="outline"
            className="border-white/20 text-zinc-400 hover:text-white font-secondary text-xs px-4 py-2 h-auto rounded-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || (!selectedAvatar && !generatedAvatar)}
            className="bg-white text-black hover:bg-zinc-200 font-secondary text-xs px-6 py-2 h-auto rounded-none"
            data-testid="save-avatar-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfilePicturePicker;
